<script setup>
import {onMounted, ref} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {definePage} from "unplugin-vue-router/runtime";
import {useAuthenticationStore} from '@/stores/authentication.js';
import {useMessageStore} from "@/stores/message.js";
import logoWhite from "@/assets/logo_full_white.svg"
import eyeIcon from "@/assets/eye.svg";
import eyeSlashIcon from "@/assets/eye-slash.svg";

import createApiService from '@/services/api';
import {useAdminStore} from "@/stores/admin.js";

const router = useRouter();
const route = useRoute();
const api = createApiService(router);

const message = ref(null);

definePage({
  meta: {
    title: 'Inloggen',
    requiresAuth: false,
  },
})

const loading = ref(false);
const email = ref('');
const password = ref('');
const showPassword = ref(false);
const loginErrors = ref([]);
const initialLoadComplete = ref(false);

const submitLogin = async () => {
  loading.value = true;
  resetErrors();

  try {
    const response = await api.post('login', {email: email.value, password: password.value});

    if (response.status === 200) {
      const authStore = useAuthenticationStore();
      authStore.setLoginData(response.data.data.user, true);
      if (authStore.mayViewAdmin) {
        const adminStore = useAdminStore();
        adminStore.setClient(authStore.getCurrentUserId);
      }

      await router.push('/');
    }
    else {
      loginErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    loginErrors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}

const resetErrors = () => {
  loginErrors.value = [];
}

onMounted(async () => {
  const messageStore = useMessageStore();
  if (messageStore.message !== '') {
    message.value = {
      message: messageStore.message,
      type: messageStore.type,
    }
    messageStore.removeMessage();
  }

  initialLoadComplete.value = true;
});
</script>

<template>
  <the-loader v-if="loading"/>

  <v-card
    class="ma-auto pa-5"
    color="primary"
    :disabled="loading"
    max-width="500"
  >

    <v-img :src="logoWhite" max-width="50%" class="mx-auto mb-12"></v-img>

    <v-form
      v-if="initialLoadComplete"
      @submit.prevent="submitLogin"
    >
      <v-card-text class="pl-0 pr-0">

        <v-alert
          v-if="message"
          class="mx-auto mb-5"
          max-width="500"
          :color="message.type"
          variant="elevated"
        >
          {{message.message}}
        </v-alert>

        <v-alert
          v-if="loginErrors.length > 0"
          class="mx-auto mb-5"
          max-width="500"
          color="error"
          variant="elevated"
        >
          <template #text>
            <span
              v-for="(error, index) in loginErrors"
              :key="index"
            >
              {{ error }}
            </span>
          </template>
        </v-alert>

        <div class="mb-4">
          <h4 class="mb-2">E-mailadres</h4>
          <v-text-field
            v-model="email"
            placeholder="E-mailadres"
            variant="solo"
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Wachtwoord</h4>
          <v-text-field
            v-model="password"
            placeholder="Wachtwoord"
            variant="solo"
            :type="showPassword ? 'text' : 'password'"
            hide-details
          >
            <template #append-inner>
              <v-icon
                :title="showPassword ? 'Verbergen' : 'Tonen'"
                @click="showPassword = !showPassword"
                class="cursor-pointer opacity-100"
              >
                <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
              </v-icon>
            </template>
          </v-text-field>
        </div>

      </v-card-text>

      <v-card-actions class="flex-column ma-auto pa-0 mt-2 actions">
        <v-btn
          class="mb-2 w-100"
          height="54"
          type="submit"
          color="secondary"
          variant="elevated"
          block
        >
          <h4>Inloggen</h4>
        </v-btn>
        <a href="/wachtwoord-vergeten" class="text-white">
          Wachtwoord vergeten?
        </a>
      </v-card-actions>
    </v-form>

    <div
      v-else
      class="text-center py-10"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div class="text-center w-100 pb-4 mt-8">
      <a href="/registreren" class="text-white">
        Nog geen account?
      </a>
    </div>
  </v-card>


</template>

<style scoped>

.actions {
  min-height: auto;
}

</style>
